import { ref, computed, onUnmounted, watch, watchEffect } from "vue";
import { bearing as turfBearing } from "@turf/bearing";
import { distance as turfDistance } from "@turf/distance";
import type {
	MultiLineString,
	Feature,
	LineString,
	MultiPolygon,
	Polygon,
} from "geojson";
import union from "@turf/union";
import { Speed } from "@ptx-autonomous/libs/units";
import type {
	PathSegment,
	PreviewMarker,
	MissionPreviewOptions,
} from "../types/mission-preview";
import { advancedBuffer } from "../utils/advanced-buffer";

export function useMissionPreview(options: MissionPreviewOptions) {
	// Reactive state
	const playbackSpeed = ref("100");
	const isStarted = ref(false);
	const isPaused = ref(false);
	const isComplete = ref(false);
	const pathProgress = ref(0); // Percentage of path completed based on distance (0-100)

	// Animation state (non-reactive)
	let animationFrameId: number | null = null;
	let startTime: number | null = null;
	let pauseTime = 0;
	let totalPauseTime = 0;
	let lastSegmentIndex = 0;
	let lastPlaybackSpeed = parseInt(playbackSpeed.value);

	// Coverage update interval (decoupled from render loop)
	let coverageUpdateInterval: ReturnType<typeof setInterval> | null = null;
	const COVERAGE_UPDATE_RATE = 100; // 10Hz
	let currentPathIndex = 0;

	// Current position and bearing
	const currentLocation = ref<[number, number]>([
		options.plannedPath.value[0].Long,
		options.plannedPath.value[0].Lat,
	]);
	const currentBearing = ref<number>(
		turfBearing(
			[
				options.plannedPath.value[0].Long,
				options.plannedPath.value[0].Lat,
			],
			[
				options.plannedPath.value[1].Long,
				options.plannedPath.value[1].Lat,
			],
		),
	);

	// Preview marker
	const previewMarker = computed<PreviewMarker>(() => ({
		location: currentLocation.value,
		bearing: currentBearing.value,
	}));

	function getStartCoordinate(): [number, number] {
		return [
			options.plannedPath.value[0].Long,
			options.plannedPath.value[0].Lat,
		];
	}

	// Driven path visualization
	const drivenPath = ref<MultiLineString>({
		type: "MultiLineString",
		coordinates: [[getStartCoordinate()]],
	});

	function setDrivenPath(coordinates: [number, number][]) {
		drivenPath.value = {
			type: "MultiLineString",
			coordinates: [coordinates],
		};
	}

	// Coverage tracking
	const completedSwathPolygons = ref<Feature<Polygon | MultiPolygon>[]>([]);
	const lastCompletedSwathIndex = ref<number>(-1);
	const previousSwathIndex = ref<number>(-1);
	const coverage = ref<Polygon | MultiPolygon | null>(null);

	// Distance tracking for accurate progress
	const totalPathDistance = ref<number>(0);
	const cumulativeDistances = ref<number[]>([]);

	// Path segments with timing calculations
	const segments = ref<PathSegment[]>([]);

	// Calculate path segments with timing and distances
	watchEffect(() => {
		segments.value = [];
		cumulativeDistances.value = [0]; // Start with 0 distance at first point
		let totalDistance = 0;

		const turningSpeedMs = new Speed(options.turningSpeed, "mph").convert(
			"m/s",
		);
		const targetSpeedMs = new Speed(options.targetSpeed, "mph").convert(
			"m/s",
		);

		for (let i = 0; i < options.plannedPath.value.length - 1; i++) {
			const start = options.plannedPath.value[i];
			const end = options.plannedPath.value[i + 1];
			const distance = turfDistance(
				[start.Long, start.Lat],
				[end.Long, end.Lat],
				{ units: "meters" },
			);
			const isTurn =
				options.plannedPath.value[i].Label === "TURN" &&
				options.plannedPath.value[i + 1].Label === "TURN";
			const speed = isTurn ? turningSpeedMs : targetSpeedMs;
			const duration = distance / speed;

			totalDistance += distance;
			cumulativeDistances.value.push(totalDistance);

			segments.value.push({
				startIndex: i,
				endIndex: i + 1,
				distance,
				duration,
			});
		}

		totalPathDistance.value = totalDistance;
	});

	// Find all swaths in the planned path
	const swathBoundaries = computed(() => {
		const swaths: { startIndex: number; endIndex: number }[] = [];
		let swathStart: number | null = null;

		for (let i = 0; i < options.plannedPath.value.length; i++) {
			const point = options.plannedPath.value[i];

			if (point.Label === "TURN") {
				// We've hit a turn point
				if (swathStart !== null) {
					// End the current swath at this turn point (include the turn point)
					swaths.push({ startIndex: swathStart, endIndex: i });
					swathStart = null;
				}
				// Turn points don't start new swaths
			} else {
				// We're in a working section
				if (swathStart === null) {
					swathStart = i; // Start of a new swath
				}
				// Continue the current swath (don't end it until we hit a TURN)
			}
		}

		// Handle final swath if path doesn't end with a turn
		if (swathStart !== null) {
			swaths.push({
				startIndex: swathStart,
				endIndex: options.plannedPath.value.length - 1,
			});
		}

		return swaths;
	});

	// Function to find which swath contains the given path index
	function findCurrentSwath(pathIndex: number): number {
		for (let i = 0; i < swathBoundaries.value.length; i++) {
			const swath = swathBoundaries.value[i];
			if (pathIndex >= swath.startIndex && pathIndex <= swath.endIndex) {
				return i;
			}
		}
		return -1;
	}

	// Function to create a buffered polygon for a swath section
	function createSwathPolygon(
		startIndex: number,
		endIndex: number,
		currentPosition?: [number, number],
	): Feature<Polygon | MultiPolygon> | null {
		const points: [number, number][] = [];

		// Add all points in the range
		for (let i = startIndex; i <= endIndex; i++) {
			const point = options.plannedPath.value[i];
			points.push([point.Long, point.Lat]);
		}

		// If we have a current position, use it as the final point instead of the last path point
		if (currentPosition && points.length > 0) {
			points[points.length - 1] = currentPosition;
		}

		if (points.length < 2) return null;

		try {
			const swathFeature: Feature<LineString> = {
				type: "Feature",
				properties: {},
				geometry: {
					type: "LineString",
					coordinates: points,
				},
			};

			return advancedBuffer(swathFeature, {
				distance: options.implementWidth / 2,
				units: "meters",
				endCapStyle: "flat",
			});
		} catch (error) {
			console.error("Error creating swath polygon:", error);
			return null;
		}
	}

	function updateCoverage(
		currentPosition: [number, number],
		pathIndex: number,
	) {
		const currentSwathIndex = findCurrentSwath(pathIndex);

		// Complete all swaths that we've passed through (handles high-speed sampling)
		for (
			let i = lastCompletedSwathIndex.value + 1;
			i < swathBoundaries.value.length;
			i++
		) {
			const swath = swathBoundaries.value[i];

			// If we've passed the end of this swath, complete it
			if (pathIndex > swath.endIndex) {
				const polygon = createSwathPolygon(
					swath.startIndex,
					swath.endIndex,
				);
				if (polygon) {
					completedSwathPolygons.value.push(polygon);
					lastCompletedSwathIndex.value = i;
				}
			} else {
				// We haven't passed this swath yet, so stop checking
				break;
			}
		}

		// Update the previous swath index for next iteration
		previousSwathIndex.value = currentSwathIndex;

		if (currentSwathIndex === -1) {
			// Not in a working swath (probably in a turn)
			let geometry = null;

			if (completedSwathPolygons.value.length === 1) {
				geometry = completedSwathPolygons.value[0].geometry;
			} else if (completedSwathPolygons.value.length > 1) {
				try {
					const unionedCoverage = union({
						type: "FeatureCollection",
						features: completedSwathPolygons.value,
					});
					geometry = unionedCoverage?.geometry || null;
				} catch (error) {
					console.error("Error unioning completed swaths:", error);
					geometry = null;
				}
			}

			coverage.value = geometry;
			return;
		}

		// We're currently in a swath - create progressive coverage
		const currentSwath = swathBoundaries.value[currentSwathIndex];
		const currentSwathPolygon = createSwathPolygon(
			currentSwath.startIndex,
			pathIndex,
			currentPosition,
		);

		// Combine completed swaths with current partial swath
		const allPolygons = [...completedSwathPolygons.value];
		if (currentSwathPolygon) {
			allPolygons.push(currentSwathPolygon);
		}

		if (allPolygons.length === 0) {
			coverage.value = null;
			return;
		}

		try {
			let finalGeometry = null;

			if (allPolygons.length === 1) {
				// Single polygon - no need to union
				finalGeometry = allPolygons[0].geometry;
			} else if (allPolygons.length > 1) {
				// Multiple polygons - use union
				const unionedCoverage = union({
					type: "FeatureCollection",
					features: allPolygons,
				});
				finalGeometry = unionedCoverage?.geometry || null;
			}

			coverage.value = finalGeometry;
		} catch (error) {
			console.error("Error unioning coverage polygons:", error);
			coverage.value = null;
		}
	}

	function getCurrentPathIndex(): number {
		return currentPathIndex;
	}

	function startCoverageUpdates() {
		if (coverageUpdateInterval) return; // Already running

		coverageUpdateInterval = setInterval(() => {
			if (!isStarted.value || isPaused.value || isComplete.value) {
				return; // Skip if not actively animating
			}

			// Use current animation state to update coverage
			updateCoverage(currentLocation.value, getCurrentPathIndex());
		}, COVERAGE_UPDATE_RATE);
	}

	function stopCoverageUpdates() {
		if (coverageUpdateInterval) {
			clearInterval(coverageUpdateInterval);
			coverageUpdateInterval = null;
		}
	}

	// Reset when planned path changes
	watch(
		() => options.plannedPath.value,
		() => {
			stop();
			const startCoordinate = getStartCoordinate();
			setDrivenPath([startCoordinate]);
			currentLocation.value = startCoordinate;
			currentBearing.value = turfBearing(
				[
					options.plannedPath.value[0].Long,
					options.plannedPath.value[0].Lat,
				],
				[
					options.plannedPath.value[1].Long,
					options.plannedPath.value[1].Lat,
				],
			);
		},
	);

	// Animation function
	function animate(timestamp: number) {
		if (isPaused.value) {
			animationFrameId = requestAnimationFrame(animate);
			return;
		}

		const playbackSpeedInt = parseInt(playbackSpeed.value);
		if (!startTime) {
			startTime = timestamp;
			lastPlaybackSpeed = playbackSpeedInt;
		}

		// Handle playback speed changes
		if (playbackSpeedInt !== lastPlaybackSpeed) {
			const currentElapsed = timestamp - startTime - totalPauseTime;
			startTime =
				timestamp -
				(currentElapsed * lastPlaybackSpeed) / playbackSpeedInt;
			lastPlaybackSpeed = playbackSpeedInt;
		}

		const elapsed =
			((timestamp - startTime - totalPauseTime) * playbackSpeedInt) /
			1000;

		let currentSegment;
		let segmentElapsed = elapsed;
		let totalDuration = 0;

		for (const segment of segments.value) {
			totalDuration += segment.duration;
			if (segmentElapsed <= segment.duration) {
				currentSegment = segment;
				break;
			}
			segmentElapsed -= segment.duration;
		}

		if (!currentSegment || elapsed > totalDuration) {
			// Animation complete
			const finalCoordinate: [number, number] = [
				options.plannedPath.value[options.plannedPath.value.length - 1]
					.Long,
				options.plannedPath.value[options.plannedPath.value.length - 1]
					.Lat,
			];
			setDrivenPath([
				...(drivenPath.value.coordinates[0] as [number, number][]),
				finalCoordinate,
			]);
			previewMarker.value.location = [
				options.plannedPath.value[options.plannedPath.value.length - 1]
					.Long,
				options.plannedPath.value[options.plannedPath.value.length - 1]
					.Lat,
			];
			previewMarker.value.bearing = turfBearing(
				[
					options.plannedPath.value[
						options.plannedPath.value.length - 2
					].Long,
					options.plannedPath.value[
						options.plannedPath.value.length - 2
					].Lat,
				],
				[
					options.plannedPath.value[
						options.plannedPath.value.length - 1
					].Long,
					options.plannedPath.value[
						options.plannedPath.value.length - 1
					].Lat,
				],
			);

			// Complete coverage animation
			const finalPosition: [number, number] = [
				options.plannedPath.value[options.plannedPath.value.length - 1]
					.Long,
				options.plannedPath.value[options.plannedPath.value.length - 1]
					.Lat,
			];
			currentPathIndex = options.plannedPath.value.length - 1;
			// Final coverage update when animation completes
			updateCoverage(finalPosition, currentPathIndex);

			// Set progress to 100% when complete
			pathProgress.value = 100;

			isComplete.value = true;
			stopCoverageUpdates();
			if (animationFrameId) cancelAnimationFrame(animationFrameId);
			return;
		}

		// Calculate progress within current segment
		const progress = Math.min(segmentElapsed / currentSegment.duration, 1);

		// Interpolate position
		const startPoint = [
			options.plannedPath.value[currentSegment.startIndex].Long,
			options.plannedPath.value[currentSegment.startIndex].Lat,
		];
		const endPoint = [
			options.plannedPath.value[currentSegment.endIndex].Long,
			options.plannedPath.value[currentSegment.endIndex].Lat,
		];

		const currentPos = [
			startPoint[0] + (endPoint[0] - startPoint[0]) * progress,
			startPoint[1] + (endPoint[1] - startPoint[1]) * progress,
		];

		const bearing = turfBearing(startPoint, endPoint);
		previewMarker.value.location = currentPos as [number, number];
		previewMarker.value.bearing = bearing;

		currentLocation.value = currentPos as [number, number];
		currentBearing.value = bearing;

		// Update driven path
		if (currentSegment.startIndex > lastSegmentIndex) {
			const nextCoordinates: [number, number][] = [
				...(
					drivenPath.value.coordinates[0] as [number, number][]
				).slice(0, -1),
			];
			for (
				let i = lastSegmentIndex + 1;
				i <= currentSegment.startIndex;
				i++
			) {
				nextCoordinates.push([
					options.plannedPath.value[i].Long,
					options.plannedPath.value[i].Lat,
				]);
			}
			nextCoordinates.push(currentPos as [number, number]);
			setDrivenPath(nextCoordinates);
			lastSegmentIndex = currentSegment.startIndex;
		} else {
			const nextCoordinates: [number, number][] = [
				...(
					drivenPath.value.coordinates[0] as [number, number][]
				).slice(0, -1),
				currentPos as [number, number],
			];
			setDrivenPath(nextCoordinates);
		}

		// Update current path index for coverage updates
		currentPathIndex = currentSegment.endIndex;

		// Calculate path progress based on distance traveled
		const distanceTraveled =
			cumulativeDistances.value[currentSegment.startIndex] +
			currentSegment.distance * progress;
		pathProgress.value =
			totalPathDistance.value > 0
				? Math.min(
						100,
						(distanceTraveled / totalPathDistance.value) * 100,
					)
				: 0;

		animationFrameId = requestAnimationFrame(animate);
	}

	// Control functions
	function play() {
		if (isComplete.value || !isStarted.value) {
			stop();
			if (animationFrameId) {
				cancelAnimationFrame(animationFrameId);
			}
			startTime = null;
			totalPauseTime = 0;
			isStarted.value = true;
			isPaused.value = false;
			previewMarker.value.location = [
				options.plannedPath.value[0].Long,
				options.plannedPath.value[0].Lat,
			];
			setDrivenPath([getStartCoordinate()]);
			currentPathIndex = 0;
			animationFrameId = requestAnimationFrame(animate);
			startCoverageUpdates();
		} else if (isStarted.value) {
			if (!isPaused.value) {
				isPaused.value = true;
				pauseTime = performance.now();
				// Do one final coverage update when pausing to catch up to marker
				updateCoverage(currentLocation.value, getCurrentPathIndex());
			} else {
				isPaused.value = false;
				totalPauseTime += performance.now() - pauseTime;
			}
		}
		isComplete.value = false;
	}

	function stop() {
		if (animationFrameId) {
			cancelAnimationFrame(animationFrameId);
			animationFrameId = null;
		}
		stopCoverageUpdates();
		previewMarker.value.location = [
			options.plannedPath.value[0].Long,
			options.plannedPath.value[0].Lat,
		];
		previewMarker.value.bearing = turfBearing(
			[
				options.plannedPath.value[0].Long,
				options.plannedPath.value[0].Lat,
			],
			[
				options.plannedPath.value[1].Long,
				options.plannedPath.value[1].Lat,
			],
		);
		setDrivenPath([getStartCoordinate()]);
		lastSegmentIndex = 0;
		completedSwathPolygons.value = [];
		lastCompletedSwathIndex.value = -1;
		previousSwathIndex.value = -1;
		coverage.value = null;
		pathProgress.value = 0;
		currentPathIndex = 0;
		isStarted.value = false;
		isPaused.value = false;
		isComplete.value = false;
	}

	function reset() {
		stop();
		// Reset timing variables
		startTime = null;
		pauseTime = 0;
		totalPauseTime = 0;
		lastSegmentIndex = 0;
		lastPlaybackSpeed = parseInt(playbackSpeed.value);

		// Reset position to start of new path
		currentLocation.value = [
			options.plannedPath.value[0].Long,
			options.plannedPath.value[0].Lat,
		];
		currentBearing.value = turfBearing(
			[
				options.plannedPath.value[0].Long,
				options.plannedPath.value[0].Lat,
			],
			[
				options.plannedPath.value[1].Long,
				options.plannedPath.value[1].Lat,
			],
		);

		// Reset progressive coverage tracking
		completedSwathPolygons.value = [];
		lastCompletedSwathIndex.value = -1;
		previousSwathIndex.value = -1;
		coverage.value = null;
		pathProgress.value = 0;
	}

	// Cleanup
	onUnmounted(() => {
		stop();
	});

	return {
		// State
		playbackSpeed,
		isStarted,
		isPaused,
		isComplete,
		pathProgress,
		currentLocation,
		currentBearing,
		previewMarker,
		drivenPath,
		segments,
		coverage,

		// Controls
		play,
		stop,
		reset,
	};
}
